import { getEnv } from '@/utils/helpers'
import { appendUrlQueryParams } from '@/utils/url.utils'
import type { ClientActionEvent } from '../types'

/** Base64-encodes a UTF-8 string (the SSE endpoint expects `platformMeta`/`businessMeta` as base64 JSON). */
export function utf8ToBase64(str: string): string {
    return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1) => String.fromCharCode(parseInt(p1, 16))))
}

export type ElaraToolEventKind = 'call' | 'start' | 'end'

export interface ElaraToolCallDescriptor {
    id: string
    name: string
    args?: any
}

export type ElaraStreamPhase = 'thinking' | 'tool-calling' | 'streaming' | 'done' | 'error'

/**
 * Callbacks emitted by the ELARA SSE transport. All optional — the global panel, the consultation
 * provider and the chat page each subscribe to the subset they render.
 */
export interface ElaraTransportCallbacks {
    onProcessingChange?: (processing: boolean) => void
    /** Accumulated response text (called on every delta and once more on completion) */
    onResponse?: (response: string) => void
    onClientAction?: (event: ClientActionEvent) => void
    /** Called on every individual token delta — drives streaming display */
    onStreamToken?: (token: string, accumulated: string) => void
    /** Called when the phase transitions: thinking → tool-calling → streaming → done */
    onPhaseChange?: (phase: ElaraStreamPhase, error?: string) => void
    /** Called when tool call events arrive */
    onToolEvent?: (event: ElaraToolEventKind, tools: ElaraToolCallDescriptor[], toolResult?: string) => void
    /** Server sent the persisted chat history (on completion) */
    onHistory?: (chatHistory: any[]) => void
    /** Session id resolved for this stream (new sessions get it from the first event) */
    onSessionId?: (sessionId: string) => void
    /** Stream finished normally */
    onComplete?: (sessionId: string | null, output: string) => void
    /** Stream failed (server error event or connection error) */
    onError?: (error: string) => void
}

export interface ElaraTransportConfig extends ElaraTransportCallbacks {
    getAccessToken: () => string | null
    /**
     * Agent graph override. Text chat never sets it — the server resolves every request to the
     * single `elara` orchestrator. Only the realtime (voice) loop passes `realtime-diagnostic`.
     */
    agentType?: string
    /** LLM provider/model override (e.g., 'openai/gpt-4o') */
    model?: string
}

/** Per-request envelope. `businessMeta` is the page-context envelope described in docs/elara-core-plan.md. */
export interface ElaraSendOptions {
    /**
     * Session to continue. `null` forces a new session. When the key is omitted the transport
     * keeps using the session it captured from the previous stream.
     */
    sessionId?: string | null
    businessMeta?: Record<string, any>
    platformMeta?: Record<string, any>
    language?: string
    /** Per-call model override (wins over the config-level one) */
    model?: string
}

export interface ElaraTransport {
    send: (message: string, options?: ElaraSendOptions) => void
    close: () => void
    getSessionId: () => string | null
    setSessionId: (sessionId: string | null) => void
    isStreaming: () => boolean
}

/**
 * The single SSE client for `GET /medconnect-agent/chat/stream/:sessionId?`.
 * Used by the global ELARA panel, the consultation provider and the full chat page.
 */
export function createElaraTransport(config: ElaraTransportConfig): ElaraTransport {
    let eventSource: EventSource | null = null
    let sessionId: string | null = null
    let streaming = false

    function setProcessing(value: boolean) {
        streaming = value
        config.onProcessingChange?.(value)
    }

    function close() {
        if (eventSource) {
            eventSource.close()
            eventSource = null
        }
        setProcessing(false)
    }

    function buildStreamUrl(message: string, options: ElaraSendOptions): string {
        const accessToken = config.getAccessToken() || ''
        const sessionPath = sessionId ? `/${sessionId}` : ''
        const baseUrl = `${getEnv('VITE_API_URL') || 'http://localhost:3000'}/medconnect-agent/chat/stream${sessionPath}`

        const queryParams: Record<string, string> = {
            message,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }
        if (accessToken) queryParams.authorization = `Bearer ${encodeURIComponent(accessToken)}`
        if (config.agentType) queryParams.agentType = config.agentType
        if (options.platformMeta !== undefined) queryParams.platformMeta = utf8ToBase64(JSON.stringify(options.platformMeta))
        if (options.businessMeta !== undefined) queryParams.businessMeta = utf8ToBase64(JSON.stringify(options.businessMeta))
        if (options.language) queryParams.language = options.language
        const model = options.model || config.model
        if (model) queryParams.model = model

        return appendUrlQueryParams(baseUrl, queryParams)
    }

    function handleToolEnd(data: any) {
        const tools: ElaraToolCallDescriptor[] = Array.isArray(data.toolCalls) ? data.toolCalls : []
        config.onToolEvent?.('end', tools, data.toolResult)

        if (!data.toolResult || !config.onClientAction) return
        try {
            const result = typeof data.toolResult === 'string' ? JSON.parse(data.toolResult) : data.toolResult
            if (result?.type === 'client_action') {
                config.onClientAction({ type: 'client_action', actionId: result.actionId, params: result.params })
            }
        } catch {
            // Tool result is not JSON — ignore
        }
    }

    function send(message: string, options: ElaraSendOptions = {}) {
        close()
        if ('sessionId' in options) sessionId = options.sessionId ?? null

        setProcessing(true)
        config.onPhaseChange?.('thinking')

        const es = new EventSource(buildStreamUrl(message, options))
        eventSource = es

        let fullResponse = ''
        let hasReceivedToken = false

        es.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data)
                if (data.type === 'heartbeat') return

                if (data.sessionId && data.sessionId !== sessionId) {
                    sessionId = data.sessionId
                    config.onSessionId?.(data.sessionId)
                }

                if (Array.isArray(data.chatHistory)) {
                    config.onHistory?.(data.chatHistory)
                }

                if (data.isDelta && typeof data.token === 'string') {
                    if (!hasReceivedToken) {
                        hasReceivedToken = true
                        config.onPhaseChange?.('streaming')
                    }
                    fullResponse += data.token
                    config.onStreamToken?.(data.token, fullResponse)
                    config.onResponse?.(fullResponse)
                }

                if (data.toolEvent === 'call' && Array.isArray(data.toolCalls)) {
                    config.onPhaseChange?.('tool-calling')
                    config.onToolEvent?.('call', data.toolCalls)
                }
                if (data.toolEvent === 'start' && Array.isArray(data.toolCalls)) {
                    config.onToolEvent?.('start', data.toolCalls)
                }
                if (data.toolEvent === 'end') {
                    handleToolEnd(data)
                }

                if (data.error) {
                    config.onPhaseChange?.('error', data.error)
                    config.onError?.(String(data.error))
                    close()
                    return
                }

                if (data.complete) {
                    // Non-streaming paths send the full response in data.output
                    const finalText = fullResponse || data.output || ''
                    if (finalText && !hasReceivedToken) config.onPhaseChange?.('streaming')
                    config.onPhaseChange?.('done')
                    close()
                    if (finalText) config.onResponse?.(finalText)
                    config.onComplete?.(sessionId, finalText)
                }
            } catch (error) {
                console.error('[ElaraTransport] SSE parsing error:', error)
                config.onPhaseChange?.('error', 'Stream parse error')
                config.onError?.('parse')
                close()
            }
        }

        es.onerror = () => {
            console.error('[ElaraTransport] SSE connection error')
            config.onPhaseChange?.('error', 'Connection lost')
            config.onError?.('connection')
            close()
        }
    }

    return {
        send,
        close,
        getSessionId: () => sessionId,
        setSessionId: (id) => { sessionId = id },
        isStreaming: () => streaming,
    }
}
